/* eslint-disable react/prop-types */
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { includes } from 'lodash';
import { useContext, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import StatusPill from '../../../../v2/js/components/StatusPill';
import { StatusCodes, WorkflowScopes } from '../../../../v2/js/constants/workflows';
import { getStatusTextStyle } from '../../../../v2/js/utils/statusStyles';
import { ConnectedFacility, ConnectionsService, UserTypeEnum } from '../../../api';

import { SessionContext } from '../../contexts/SessionContext';
import { SIZE_LG, useBreakpoint } from '../../hooks/breakpoint';
import { connectionRequestsQueryKeys } from '../../query-keys-factories/connection-requests';
import { Card } from '../Card';

import { FacilityCard } from '../FacilityCard';

import { Table } from '../Table';

const LIST_SIZE = 3;

interface ConnectedFacilitiesListProps {
  providerId: string;
  onConnectionSelect?: (connectionId: string | null) => void;
}
/* eslint-disable sonarjs/cognitive-complexity*/
export const ConnectedFacilitiesList = ({
  providerId,
  onConnectionSelect,
}: ConnectedFacilitiesListProps) => {
  const [showFullList, setShowFullList] = useState(false);
  const breakpoint = useBreakpoint();
  const isGrid = useMemo(() => includes(breakpoint, SIZE_LG), [breakpoint]);
  const [effectiveListSize, setEffectiveListSize] = useState<number>(LIST_SIZE);
  const { user } = useContext(SessionContext);
  const userIsProvider = user?.user_type == UserTypeEnum.PROVIDER;

  const connectionQueryParams: Parameters<
    typeof ConnectionsService.connectionsConnectedFacilityList
  >[0] = {
    provider: providerId,
    ordering: '-updated_at',
  };
  const { isLoading, data } = useQuery({
    queryKey: connectionRequestsQueryKeys.list(connectionQueryParams),
    queryFn: async () => {
      return ConnectionsService.connectionsConnectedFacilityList({
        provider: providerId,
        ordering: '-updated_at',
      });
    },
    enabled: !!providerId,
    select: (data) => data.results,
  });

  const list = useMemo(() => {
    const fullList = (data as unknown as ConnectedFacility[]) || [];

    // Sort function to sequence items: Pending > Completed > Rejected > Canceled
    const sortedList = [...fullList].sort((a, b) => {
      const getStatusWeight = (status: string | undefined) => {
        if (status === StatusCodes.REQUESTED) {
          return 1; // Top priority
        }
        if (status === StatusCodes.ACTIVE) return 2;
        if (status === StatusCodes.PROV_ACTION_REQ) return 3;
        if (status === StatusCodes.AWAIT_CLEAR) return 4;
        if (status === StatusCodes.PEND_FAC_REV) return 5;
        if (status === StatusCodes.PEND_CONFIRM) return 6;
        if (status === StatusCodes.PAUSED) return 7;
        if (status === StatusCodes.WITHDRAWN) return 8;
        if (status === StatusCodes.DECLINED) return 9;
        if (status === StatusCodes.TERMINATED) return 10;
        if (status === StatusCodes.CANCELED) return 11;

        return 99; // Everything else
      };

      const weightA = getStatusWeight(a.current_status.code);
      const weightB = getStatusWeight(b.current_status.code);

      if (weightA !== weightB) {
        return weightA - weightB;
      }
      return 0;
    });

    // Count pending items
    // const pendingCount = sortedList.filter(
    //   (item) =>
    //     item.current_status.code === StatusCodes.REQUESTED ||
    //     item.current_status.code === StatusCodes.AWAIT_CLEAR
    // ).length;

    // Use the larger of pendingCount or LIST_SIZE
    // const effectiveListSize = Math.max(pendingCount, LIST_SIZE);
    const effectiveListSize = LIST_SIZE;
    setEffectiveListSize(effectiveListSize);

    if (showFullList) {
      return sortedList;
    }

    return sortedList.slice(0, effectiveListSize);
  }, [data, showFullList]);

  const columns = useMemo(
    () => [
      {
        header: 'Facility Name',
        accessorFn: (row) => ({
          name: row.facility.name,
          id: row.facility.id,
          opportunity: row.opportunity,
        }),
        cell: (props) => {
          const { name, id, opportunity } = props.getValue();
          return (
            <div className="d-flex align-items-center gap-2">
              <Link to={`/app/organization/facilities/${id}`}>{name}</Link>
              {opportunity && (
                <Link
                  to={`/app/organization/opportunities/${opportunity.id}`}
                  className="ms-1"
                  title="View Opportunity"
                >
                  <i className="fa-solid fa-arrow-up-right-from-square" />
                </Link>
              )}
            </div>
          );
        },
        size: 25,
        minSize: 0,
      },
      {
        header: 'Location',
        accessorFn: (data) => `${data.facility.city}, ${data.facility.state}`,
        cell: (props) => {
          const city_state = props.getValue();
          return <div className="small">{city_state}</div>;
        },
        size: 15,
        minSize: 0,
      },
      {
        header: 'Hourly Rates',
        accessorFn: (data) => ({
          day_rate: userIsProvider ? data.shift_rate_day : data.facility_rate_day,
          night_rate: userIsProvider ? data.shift_rate_night : data.facility_rate_night,
        }),
        cell: (props) => {
          const rates = props.getValue();
          const hasRate = (rate) =>
            rate !== null && rate !== undefined && rate !== 0 && rate !== '0';
          return (
            <div className="small">
              {hasRate(rates.day_rate) && `$${rates.day_rate} Day`}
              {hasRate(rates.day_rate) && hasRate(rates.night_rate) && ' / '}
              {hasRate(rates.night_rate) && `$${rates.night_rate} Night`}
            </div>
          );
        },
        size: 8,
        minSize: 0,
      },
      {
        header: 'Status',
        accessorFn: (data) => ({
          current_status: data.current_status,
          current_reason: data.current_reason,
        }),
        cell: (props) => {
          const facility_relationship = props.getValue();
          return (
            <>
              <StatusPill
                scope={WorkflowScopes.ConnectedFacility}
                status={facility_relationship.current_status}
              />
              {facility_relationship.current_reason && (
                <div
                  className={twMerge(
                    clsx('small mt-1', getStatusTextStyle(facility_relationship.current_status))
                  )}
                >
                  {facility_relationship.current_reason.title}
                </div>
              )}
            </>
          );
        },
        size: 20,
        minSize: 0,
      },
      {
        header: ' ',
        accessorFn: (row: ConnectedFacility) => ({
          id: row.id,
          current_status: row.current_status,
        }),
        cell: (props) => {
          const facility_relationship = props.getValue();

          return (
            <div className="d-flex gap-2 align-items-center">
              <Button
                variant="outline-primary"
                className="btn btn-drop-shadow-sm px-3"
                size="sm"
                onClick={() => onConnectionSelect?.(facility_relationship.id)}
              >
                Open
              </Button>
            </div>
          );
        },
        size: 25,
        minSize: 0,
      },
    ],
    [onConnectionSelect]
  );

  return list?.length > 0 ? (
    <>
      <Card className="mb-4 p-3">
        <div className="p-2">
          <h4 className="mb-3">Facilities</h4>
          {isGrid && <Table columns={columns} data={list} isLoading={isLoading} />}
          {!isGrid &&
            list.map((item: ConnectedFacility, index) => (
              <FacilityCard data={item} key={index} onConnectionSelect={onConnectionSelect} />
            ))}
        </div>
        {(data as unknown as [])?.length > effectiveListSize && (
          <Button
            variant="link"
            className="mx-auto my-1"
            onClick={() => setShowFullList(!showFullList)}
          >
            {showFullList ? 'Show Less' : 'Show More'}
          </Button>
        )}
      </Card>
    </>
  ) : null;
};
